import React, { useCallback, useState } from 'react';

import { Link } from 'react-router-dom';
import { Button, IconButton } from '@material-ui/core';
import { MdEdit, MdDelete, MdPhoneIphone } from 'react-icons/md';

import API from 'utils/api';
import { useSources, useTableStateRestore } from 'hooks';
import { ButtonContainer, ConfirmDialog, Table, Tooltip } from 'components';
import { hasAnyRole } from 'utils/credentials';
import { ROLES } from 'consts';
import { call } from 'utils';

import styles from './ChannelList.module.scss';

const ChannelList = props => {
  const [dialog, setDialog] = useState({});

  const sources = useSources();
  const { tableProps, forwardLink } = useTableStateRestore(props);

  const fetchData = useCallback(
    ({ search, ...rest }) =>
      API.getChannels(
        {
          ...rest,
          query: search
            ? JSON.stringify({ name: { $regex: search, $options: 'i' } })
            : undefined,
          select: 'name token mobile event',
        },
        sources
      ),
    [sources]
  );

  const renderMobile = data =>
    data.mobile ? (
      <div className={styles.mobile}>
        <Tooltip title="Default channel for Mobile Apps" placement="top">
          <MdPhoneIphone color="#ff3b00" fontSize="25px" />
        </Tooltip>
      </div>
    ) : null;

  const renderActions = data => {
    return (
      <div>
        <IconButton
          disabled={!!data.hasDependencies}
          color="primary"
          onClick={() => setDialog({ confirm: data })}
        >
          <Tooltip title="Click to delete this channel" placement="top">
            <MdDelete />
          </Tooltip>
        </IconButton>

        <IconButton
          disabled={!hasAnyRole([ROLES.CHANNELADMIN, ROLES.ADMIN, ROLES.SUPERADMIN])}
          color="primary"
          component={Link}
          to={forwardLink('editor', { ...data, id: data._id })}
        >
          <Tooltip title="Click to edit this Channel" placement="top">
            <MdEdit />
          </Tooltip>
        </IconButton>
      </div>
    );
  };

  const renderControls = data => {
    const { _id: channelId, name: channelName, event: eventId } = data;

    const button = (label, pathname, data) => (
      <Button
        className={styles.button}
        variant="contained"
        color="primary"
        component={Link}
        to={forwardLink(pathname, { channelId, channelName, ...data })}
      >
        {label}
      </Button>
    );

    return (
      <div className={styles.controls}>
        {button('Events', 'events')}
        {button('Global', 'messages', { eventId })}
        {button('Inbox', 'inbox', { eventId })}
      </div>
    );
  };

  const onCloseDialog = () => setDialog({});

  return (
    <div className={styles.channelList}>
      <ButtonContainer position="top">
        <Button
          variant="contained"
          color="primary"
          component={Link}
          to={forwardLink('editor')}
        >
          create new channel
        </Button>
      </ButtonContainer>

      <Table
        {...tableProps}
        data={fetchData}
        title="Channels"
        columns={[
          {
            title: 'Mobile',
            field: 'mobile',
            align: 'center',
            defaultSort: 'desc',
            render: renderMobile,
          },
          { title: 'Name', field: 'name', weight: 2 },
          { title: 'Token', field: 'token', weight: 2 },
          {
            title: 'Actions',
            sorting: false,
            align: 'center',
            render: renderActions,
          },
          {
            sorting: false,
            render: renderControls,
            weight: 2,
          },
        ]}
      />

      <ConfirmDialog
        open={!!dialog.confirm}
        onClose={yes => {
          onCloseDialog();

          if (yes) {
            API.deleteChannel(dialog.confirm._id, sources).then(() =>
              call(tableProps.tableRef.current.reload)
            );
          }
        }}
        title="Delete Channel"
        message={[
          'Are you sure you want to delete this channel:',
          `'${(dialog.confirm || {}).name}'?`,
        ]}
      />
    </div>
  );
};

export default ChannelList;
